import { useQueryClient } from '@tanstack/react-query'
import { Play, Pause } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  usePatchAgentsById,
  getGetAgentsQueryKey,
} from '@/api/control/control-plane/control-plane'
import type { AgentDto } from '@/api/control/missionImpactAPI.schemas'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface ToggleAgentStatusButtonProps {
  orgId: string
  agent: AgentDto
}

/**
 * Inline button to toggle an agent between Active and Inactive status.
 * Only renders for agents that are already registered (Active or Inactive).
 */
export function ToggleAgentStatusButton({ orgId, agent }: ToggleAgentStatusButtonProps) {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const { handleApiError } = useError()

  const isActive = agent.status === 'Active'
  const newStatus = isActive ? 'Inactive' : 'Active'

  const { mutate: patchAgent, isPending } = usePatchAgentsById({
    mutation: {
      onSuccess: () => {
        toast.success(
          isActive ? t.agents.toggleStatus.deactivated : t.agents.toggleStatus.activated
        )
        void queryClient.invalidateQueries({
          queryKey: getGetAgentsQueryKey(orgId),
        })
      },
      onError: (error) => {
        handleApiError(error, { context: 'ToggleAgentStatusButton.patchAgent' })
      },
    },
  })

  const handleToggle = () => {
    patchAgent({ orgId, agentId: agent.id, data: { status: newStatus } })
  }

  // Only show for Active or Inactive agents, not Unregistered
  if (agent.status !== 'Active' && agent.status !== 'Inactive') {
    return null
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleToggle}
      disabled={isPending}
      aria-label={isActive ? t.agents.toggleStatus.deactivate : t.agents.toggleStatus.activate}
    >
      {isActive ? (
        <Pause className="h-4 w-4" />
      ) : (
        <Play className="h-4 w-4" />
      )}
    </Button>
  )
}
